// /plugins/theme.client.ts (or .ts if both)
export default defineNuxtPlugin((nuxtApp) => {
  const { site } = useSite();
  const { loggedIn } = useUserSession();
  const { publicApiUrl } = useApiUrl();

  watch([site, loggedIn], ([current, isLoggedIn]) => {
    if (!current?.id || !isLoggedIn) return;

    nuxtApp.runWithContext(async () => {
      try {
        // Use public URL for browser-visible assets (img src, CSS background, etc.)
        const base = `${publicApiUrl.value}/v1/file/${current.id}/download/theme`;

        // CSS — guard by value so middleware-set URL isn't overwritten unnecessarily
        const cssHref = useState<string | null>('site-css', () => null);
        const nextCss = `${base}/themecss`;
        if (cssHref.value !== nextCss) cssHref.value = nextCss;

        // favicon (svg primary, png fallback via <link> order)
        // Uses same keys as middleware so unhead deduplicates
        const favSvg = `${base}/faviconsvg`;
        const favPng = `${base}/faviconpng`;
        useHead({
          link: [
            { rel: 'icon', type: 'image/svg+xml', href: favSvg, key: 'favicon-svg' },
            { rel: 'icon', type: 'image/png', href: favPng, key: 'favicon-png' },
          ],
        });

        // logo URLs for composable — guard by value
        const logoKeys = [
          ['site-logo', `${base}/logosvg`],
          ['site-logo-svg', `${base}/logosvg`],
          ['site-logo-png', `${base}/logopng`],
          ['site-logo-jpg', `${base}/logojpg`],
          ['site-logo-jpeg', `${base}/logojpeg`],
        ] as const;
        for (const [key, url] of logoKeys) {
          const state = useState<string | null>(key, () => null);
          if (state.value !== url) state.value = url;
        }

        // quotes
        const headers = { 'x-site-id': current.id };
        const quotes = useState<any[]>('site-quotes', () => []);
        try {
          const data = await useApiFetch<string>(`/v1/file/${current.id}/download/theme/quotesjson`, { headers });
          quotes.value = Array.isArray(data) ? data : [];
        } catch { quotes.value = []; }

        // background — guard by value
        const bgState = useState<string | null>('site-background', () => null);
        const nextBg = `${base}/backgroundsvg`;
        if (bgState.value !== nextBg) bgState.value = nextBg;
      } catch (err) {
        console.error('Failed to load site theme', err);
      }
    });
  }, { immediate: true });
});
